import { useState } from "react";
import HeroIcon from "./HeroIcon";
import { Workflow } from "./interface";
import TruncateTooltip from "./TruncateTooltip";
import { getWorkflowOutputCardPills } from "./utils";

interface Props {
  workflow: Workflow;
  onSelect: (workflow: Workflow) => void;
}

const thumbnailOrientationCache = new Map<string, "landscape" | "portrait">();
const failedThumbnailCache = new Set<string>();

function WorkflowCard(props: Props) {
  const { workflow } = props;
  const { pills: outputPills, overflowCount } = getWorkflowOutputCardPills(workflow);
  const cachedOrientation = workflow.previewFileId ? thumbnailOrientationCache.get(workflow.previewFileId) ?? null : null;
  const hasPreviouslyFailed = workflow.previewFileId ? failedThumbnailCache.has(workflow.previewFileId) : false;

  const [showPreview, setShowPreview] = useState(!hasPreviouslyFailed);
  const [thumbnailOrientation, setThumbnailOrientation] = useState<"landscape" | "portrait" | null>(cachedOrientation);

  function handleImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const { naturalWidth, naturalHeight } = e.currentTarget;
    const orientation = naturalHeight > naturalWidth ? "portrait" : "landscape";
    setThumbnailOrientation(orientation);
    if (workflow.previewFileId) {
      thumbnailOrientationCache.set(workflow.previewFileId, orientation);
    }
  }

  function handleImageError() {
    setShowPreview(false);
    if (workflow.previewFileId) {
      failedThumbnailCache.add(workflow.previewFileId);
    }
  }

  function handleOnClick() {
    if (workflow.status === "INCOMPLETE")
      return window.open(`https://app.portant.co/w/${workflow.id}`, "_blank");

    props.onSelect(workflow);
  }

  return (
    <div
      className="card-hover-ring h-80 rounded-lg border border-gray-200 bg-white p-2.5 flex flex-col cursor-pointer overflow-hidden"
      onClick={handleOnClick}
    >
      <div
        className="relative border border-gray-200 rounded h-[180px] flex-shrink-0 overflow-hidden"
        style={{ backgroundColor: `${workflow.color}1A` }}
      >
        {workflow.status === "INCOMPLETE" &&
          <div className="absolute top-2 right-2 z-10 bg-yellow text-white text-xs px-2 py-0.5 rounded font-semibold">
            Incomplete
          </div>
        }
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ color: workflow.color }}
        >
          <HeroIcon icon={workflow.icon} className="w-16 h-16" />
        </div>
        {(showPreview && workflow.previewFileId) &&
          <img
            className={`transition-opacity duration-200 ${thumbnailOrientation ? "opacity-100" : "opacity-0"} ${thumbnailOrientation === "portrait"
              ? "absolute top-4 left-4 w-[calc(100%-2rem)] h-auto border border-gray-200 rounded-t-sm"
              : "absolute inset-x-0 top-1/2 -translate-y-1/2 w-full h-auto border border-gray-200"
              }`}
            onLoad={handleImageLoad}
            onError={handleImageError}
            src={`https://drive.google.com/thumbnail?authuser=0&sz=w320&id=${workflow.previewFileId}`}
            alt="Thumbnail"
          />
        }
      </div>
      <div className="flex-1 flex flex-col gap-4 px-3 pt-4 pb-3 justify-start">
        <div className="flex items-center justify-between gap-2 min-w-0 w-full">
          <div
            className="flex items-center justify-center h-6 w-6 rounded flex-shrink-0"
            style={{ backgroundColor: workflow.color }}
          >
            <HeroIcon icon={workflow.icon} className="w-5 h-5 text-white" />
          </div>
          {(outputPills.length > 0 || overflowCount > 0) &&
            <div className="flex items-center gap-1 flex-shrink-0 min-w-0">
              {outputPills.map((pill) => (
                <div
                  key={pill.iconUrl}
                  className="flex h-6 items-center gap-0.5 border border-gray-200 rounded-md px-1.5 shrink-0"
                >
                  <img src={pill.iconUrl} alt="" className="h-3 w-2.5 shrink-0 object-contain" />
                  <span className="text-xs text-gray-500 font-gilroy leading-none">{pill.label}</span>
                </div>
              ))}
              {overflowCount > 0 &&
                <div className="flex h-6 min-w-[24px] items-center justify-center border border-gray-200 rounded-md px-1.5 shrink-0">
                  <span className="text-xs text-gray-500 font-gilroy leading-none whitespace-nowrap">
                    +{overflowCount}
                  </span>
                </div>
              }
            </div>
          }
        </div>
        <TruncateTooltip
          label={workflow.name}
          className="font-gilroy font-semibold text-lg text-black leading-tight"
          textClassName="line-clamp-2"
        />
      </div>
    </div>
  )
}

export default WorkflowCard;
